import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Breadcrumb,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { FilterOutlined, UndoOutlined } from "@ant-design/icons";

import { getRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";

const { Search } = Input;

const reasonOptions = [
  { label: "Transit Damage", value: "Transit Damage" },
  { label: "Short Supplied", value: "Short Supplied" },
  { label: "Packaging Damage", value: "Packaging Damage" },
  { label: "Wrong Item", value: "Wrong Item" }
];

const statusOptions = [
  { label: "Pending Action", value: "Pending Action" },
  { label: "Return Initiated", value: "Return Initiated" },
  { label: "Closed", value: "Closed" },
];

const QuarantinedMaterialQueue = () => {
  const navigate = useNavigate();
  const user = authUserData();
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [reasonFilter, setReasonFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);

  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    reason: undefined,
    status: undefined,
  });
  const [clickedFilterButton, setClickedFilterButton] = useState(false);

  const didMountRef = useRef(false);

  const userDisplay = useMemo(() => {
    const first = (user?.first_name || "").trim();
    const last = (user?.last_name || "").trim();
    const fullName = `${first} ${last}`.trim();
    return fullName || user?.email || user?.username || user?.mobile_number || "—";
  }, [user]);

  const fetchQuarantinedMaterials = async (params = {}) => {
    try {
      setLoading(true);
      const res = await getRequest("quarantined-materials/", params);
      const payload = res?.data;
      const count = payload?.count ?? 0;
      const results = payload?.results;
      const rawList =
        results?.data ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
      const list = Array.isArray(rawList) ? rawList : [];
      const mapped = list.map((item) => ({
        key: item.id ?? `row-${item.sr_no ?? item.id}`,
        id: item.id,
        sr_no: item.sr_no,
        itemName: item.item_name ?? "—",
        rejectedQty: item.remaining_qty ?? 0,
        rejectionDetails: Array.isArray(item.rejection_details)
          ? item.rejection_details
          : [],
        uom: item.uom ?? "—",
        sourceRef: item.source_ref ?? "—",
        reason: item.rejection_reason ?? item.reason ?? "—",
        status: item.status ?? "Pending Return Entry",
        confirmation_date: item.confirmation_date,
        site_id: item.site_id,
        site_name: item.site_name,
        invoice_number: item.invoice_number,
        confirmed_by: item.confirmed_by,
      }));
      setTableData(mapped);
      setTotalCount(Number(count) || 0);
    } catch (err) {
      ShowErrorMsg(err?.response?.data?.error || err?.message || "Failed to load quarantined materials");
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const rejectionDetailColumns = useMemo(
    () => [
      { title: "Source", dataIndex: "source", key: "source" },
      { title: "Stage", dataIndex: "stage", key: "stage" },
      {
        title: "Qty.",
        dataIndex: "quantity",
        key: "quantity",
        align: "right",
      },
      { title: "Reason", dataIndex: "reason", key: "reason"},
      {
        title: "Confirmed / Inspected By",
        key: "by",
        render: (_, row) =>
          row?.confirmed_by ?? row?.inspected_by ?? "—",
      },
      {
        title: "Email",
        key: "email",
        render: (_, row) =>
          row?.confirmed_by_email ?? row?.inspected_by_email ?? "—",
      },
      {
        title: "Phone",
        key: "phone",
        render: (_, row) =>
          row?.confirmed_by_phone ?? row?.inspected_by_phone ?? "—",
      },
    ],
    [],
  );

  useEffect(() => {
    fetchQuarantinedMaterials({ page: 1, pagesize: state.pagesize });
  }, []);

  const buildQueryParams = useMemo(() => {
    const params = { page: state.page, pagesize: state.pagesize };
    if (appliedFilters.reason) params.rejection_reason = appliedFilters.reason;
    if (appliedFilters.status) params.status = appliedFilters.status;
    if (appliedFilters.search) params.search = appliedFilters.search;
    return params;
  }, [appliedFilters.reason, appliedFilters.search, appliedFilters.status, state.page, state.pagesize]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchQuarantinedMaterials(buildQueryParams);
  }, [buildQueryParams]);

  const handleApplyFilters = () => {
    setClickedFilterButton(true);
    setState((prev) => ({ ...prev, page: 1 }));
    setAppliedFilters({
      search: (searchValue || "").trim(),
      reason: reasonFilter,
      status: statusFilter,
    });
  };

  const handleResetFilters = () => {
    setClickedFilterButton(false);
    setSearchValue("");
    setReasonFilter(undefined);
    setStatusFilter(undefined);
    setState((prev) => ({ ...prev, page: 1 }));
    setAppliedFilters({ search: "", reason: undefined, status: undefined });
  };

  const handleTableChange = (pagination) => {
    setState((prev) => ({
      ...prev,
      page: pagination.current,
      pagesize: pagination.pageSize,
    }));
  };

  const columns = [
    {
      title: "Item Name (AI Read)",
      dataIndex: "itemName",
      key: "itemName",
      render: (value) => <span className="muted-text">{value}</span>,
    },
    {
      title: "Rejected Qty.",
      dataIndex: "rejectedQty",
      key: "rejectedQty",
      align: "right",
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
    },
    {
      title: "Source Ref. (GRN/TRF)",
      dataIndex: "sourceRef",
      key: "sourceRef",
    },
    {
      title: "Rejection Reason & Context",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
        size="small"
          type="primary"
          className="SaveGradiantBtn"
          onClick={() =>
            navigate(`/return-quarantined-material/${record.id}`)
          }
        >
          Initiate Return
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              placement="right"
              title="Review quarantined materials (rejected/damaged) pending return or disposal."
            >
              <h3>Quarantined Material Queue (Rejected / Damaged Stock)</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Quarantined Material Queue</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="po-good-header">
            <div className="po-header-details" style={{ margin: "0" }}>
              <div className="po-row">
                <strong>User :</strong>
                <span>{userDisplay}</span>
              </div>
            </div>

            <Space wrap size={10}>
              <div className="searchwrp">
                <Space direction="vertical">
                  <Search
                    placeholder="Search by Item or GRN Ref..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    allowClear
                    prefix={
                      <img
                        src={SearchIcon}
                        alt="SearchIcon"
                        className="SearchIcon"
                        width={22}
                      />
                    }
                    style={{ width: 280 }}
                  />
                </Space>
              </div>

              <Select
                allowClear
                placeholder="Select rejection reason"
                value={reasonFilter}
                options={reasonOptions}
                onChange={setReasonFilter}
                style={{ width: 200 }}
              />
         

              <Button
                type="primary"
                htmlType="button"
                icon={
                  <Tooltip title="Apply" color="#094E8F">
                    <FilterOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
                onClick={
                  searchValue || reasonFilter || statusFilter
                    ? handleApplyFilters
                    : null
                }
              />

              <Button
                htmlType="button"
                type="primary"
                icon={
                  <Tooltip title="Reset" color="#094E8F">
                    <UndoOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                onClick={clickedFilterButton ? handleResetFilters : handleResetFilters}
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
              />
            </Space>
          </div>

          <div className="tablewrp">
            <Table
              rowKey="key"
              columns={columns}
              dataSource={tableData}
              loading={loading}
              expandable={{
                rowExpandable: (record) =>
                  (record?.rejectionDetails ?? []).length > 0,
                expandedRowRender: (record) => (
                  <div style={{ padding: "4px 8px" }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>
                      Rejected Item Details
                    </div>
                    <Table
                      rowKey={(_, idx) => `${record?.id ?? "row"}-rej-${idx}`}
                      columns={rejectionDetailColumns}
                      dataSource={record?.rejectionDetails ?? []}
                      size="small"
                      pagination={false}
                      scroll={{ x: 1000 }}
                      locale={{ emptyText: "No rejection details found." }}
                    />
                  </div>
                ),
              }}
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                defaultCurrent: state.page,
                defaultPageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                locale: {
                  items_per_page: "Items / Page",
                },
                showTotal: (total) => `Total ${total} items`,
              }}
              scroll={{ x: 1000 }}
              onChange={handleTableChange}
              locale={{
                emptyText: loading ? null : "No quarantined materials found.",
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default QuarantinedMaterialQueue;

